import React, { FC } from 'react';
import { FieldArray } from 'formik';
import { IDictionary } from '@constants/languages/types';
import { Button } from '@components/Button';
import { Checkbox } from '@components/Checkbox';
import { Select } from '@components/Select';
import { intervalsGroup } from '@constants/options/timeOptions';
import { UilCopy, UilPlus, UilTrash } from '@iconscout/react-unicons';
import './styles.scss';

export interface IWorkingHoursProps {
  day: string;
  dictionary: IDictionary;
}

export const WorkingHours: FC<IWorkingHoursProps> = ({ day, dictionary }) => (
  <FieldArray
    name={`schedule.days.${day}`}
    render={(arrayHelpers) => {
      const isInactive = !arrayHelpers.form.values.schedule.days[day].length;

      const workHours = {
        from: '',
        to: '',
      };

      const formValuesScheduleDays = arrayHelpers.form.values.schedule.days;

      const onDayCheckChange = () => {
        isInactive
          ? arrayHelpers.insert(formValuesScheduleDays[day].length, workHours)
          : arrayHelpers.form.setFieldValue(`schedule.days.${day}`, []);
      };

      const onAddIntervalClick = () => {
        arrayHelpers.insert(formValuesScheduleDays[day].length, workHours);
      };

      const onRemoveIntervalClick = (index: number) => {
        arrayHelpers.remove(index);
      };

      const onCopyIntervalsClick = () => {
        Object.keys(formValuesScheduleDays).forEach((workingDay) => {
          arrayHelpers.form.setFieldValue(
            `schedule.days.${workingDay}`,
            formValuesScheduleDays[workingDay].length
              ? formValuesScheduleDays[day]
              : formValuesScheduleDays[workingDay],
          );
        });
      };

      return (
        <li className="working-hours-item">
          <div className="working-hours-col">
            <div className="working-hours-checkbox">
              <Checkbox
                name={`${day}-check`}
                label={dictionary.physician?.table[day as keyof typeof dictionary.physician.table]}
                checked={!isInactive}
                handleChange={onDayCheckChange}
              />
            </div>
          </div>
          <div className="working-hours-col working-hours-intervals">
            {isInactive && (
              <span className="working-hours-inactive">{dictionary.labels.unavailable}</span>
            )}
            {!isInactive &&
              formValuesScheduleDays[day].map(
                (item: { from: string; to: string }, index: number) => (
                  <div
                    key={`${day}.${index}`}
                    id={`${day}.${index}`}
                    className="form-field interval"
                  >
                    <Select
                      isDisabled={index !== 0 && !formValuesScheduleDays[day].at(0).to}
                      name={`schedule.days.${day}.${index}.from`}
                      value={formValuesScheduleDays[day]?.at(index)?.from ?? ''}
                      options={intervalsGroup(index !== 0 && formValuesScheduleDays[day].at(0).to)}
                    />
                    <span className="interval-divider">-</span>
                    <Select
                      isDisabled={!formValuesScheduleDays[day].at(index).from}
                      name={`schedule.days.${day}.${index}.to`}
                      value={formValuesScheduleDays[day]?.at(index)?.to ?? ''}
                      options={intervalsGroup(item.from)}
                    />
                    {formValuesScheduleDays[day].length > 1 && (
                      <div className="interval-remove">
                        <Button
                          size="small"
                          type="button"
                          variant="danger"
                          handleClick={() => onRemoveIntervalClick(index)}
                          iconAppend={<UilTrash />}
                        />
                      </div>
                    )}
                  </div>
                ),
              )}
          </div>
          <div className="working-hours-col">
            <div className="working-hours-actions">
              <Button
                isDisabled={!(formValuesScheduleDays[day].length < 2)}
                size="small"
                type="button"
                variant="success"
                handleClick={onAddIntervalClick}
                iconPrepend={<UilPlus />}
              />
              <Button
                isDisabled={!formValuesScheduleDays[day].length}
                size="small"
                type="button"
                variant="info"
                handleClick={onCopyIntervalsClick}
                iconPrepend={<UilCopy />}
              />
            </div>
          </div>
        </li>
      );
    }}
  />
);
